/**
 * RTK Query endpoints for the approvals service (@cluster/approvals).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/approvals`.
 */
import { apiSlice } from './apiSlice';
import type {
  Approval,
  ApprovalStatus,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

export interface ApprovalListParams {
  projectId?: string;
  status?: ApprovalStatus;
}

export interface CreateApprovalDto {
  projectId: string;
  title: string;
  description?: string;
  requester?: { conversationId?: string; userId?: string; bulkOperationId?: string };
  requestedActions: Array<{ service: string; method: string; payloadSummary?: Record<string, unknown> }>;
  choices?: Array<{ id: string; label: string }>;
  expiresInSeconds?: number;
  ephemeralTokenExpirySeconds?: number;
}

export interface DecideApprovalResult {
  data: Approval;
  token?: string;
  tokenExpiresAt?: string;
}

export const approvalsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getApprovals: builder.query<Approval[], ApprovalListParams | void>({
      query: (params) => ({ url: '/approvals', params: params || undefined }),
      transformResponse: data<Approval[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Approval' as const, id: _id })),
              { type: 'Approval', id: 'LIST' },
            ]
          : [{ type: 'Approval', id: 'LIST' }],
    }),

    getApproval: builder.query<Approval, string>({
      query: (id) => `/approvals/${id}`,
      transformResponse: data<Approval>,
      providesTags: (_result, _error, id) => [{ type: 'Approval', id }],
    }),

    createApproval: builder.mutation<Approval, CreateApprovalDto>({
      query: (body) => ({ url: '/approvals', method: 'POST', body }),
      transformResponse: data<Approval>,
      invalidatesTags: [{ type: 'Approval', id: 'LIST' }],
    }),

    decideApproval: builder.mutation<
      DecideApprovalResult,
      { id: string; approve: boolean; choiceId?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/approvals/${id}/decide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Approval', id }, { type: 'Approval', id: 'LIST' }]
          : [{ type: 'Approval', id: 'LIST' }],
    }),

    cancelApproval: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/approvals/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Approval', id: 'LIST' }],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetApprovalsQuery,
  useGetApprovalQuery,
  useCreateApprovalMutation,
  useDecideApprovalMutation,
  useCancelApprovalMutation,
} = approvalsApi;